<template>
  <GbDialog
    dialog-id="impact-statement-modal"
    :visible="visible"
    @update:visible="(val: boolean) => !val && $emit('close')"
    @hide="$emit('close')"
    :show-header-content="false"
    :footerButtons="false"
    :showCloseIcon="false"
    :modal="true"
    class="!w-[718px] !max-w-[calc(100vw-2rem)]"
    :pt="{ header: { class: '!hidden' } }"
  >
    <template #bodyContent>
      <div class="px-6 py-1">
        <div class="flex flex-col gap-1 mb-6">
          <div class="text-xl leading-7 text-gray-700 uppercase">{{ subjectLabel }}</div>
          <h2 id="impact-statement-modal-disability-title" class="text-display-md leading-9 font-bold text-gray-900" tabindex="-1" autofocus>{{ disabilityLabel }}</h2>
          <p id="impact-statement-areas-label" class="text-md leading-5 text-gray-900">Select one or more impacted areas below to develop an impact statement</p>
        </div>

        <div v-if="generationError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {{ generationError }}
        </div>

        <div
          ref="groupRef"
          class="grid grid-cols-1 gap-y-3 sm:grid-cols-2"
          role="group"
          tabindex="-1"
          aria-labelledby="impact-statement-areas-label"
          :aria-describedby="showSelectionError ? 'impact-selection-error' : undefined"
        >
          <div
            v-for="option in impactOptions" 
            :key="option.value"
            class="flex items-center"
          >
            <Checkbox
              v-model="selectedImpactUuids"
              :value="option.value"
              :inputId="`impact-${option.value}`"
              :binary="false"
            />
            <label 
              :for="`impact-${option.value}`"
              class="ml-3 text-base leading-6 text-gray-900 cursor-pointer"
            >
              {{ option.label }}
            </label>
          </div>
        </div>

        <div v-if="showSelectionError" class="mt-2.5 mb-6 flex items-center gap-1 text-md leading-4 text-red-300">
          <span class="material-icons-round text-lg" aria-hidden="true">warning</span>
          <span id="impact-selection-error"><span class="sr-only">Error: </span>Select at least 1 impacted area above</span>
        </div>

        <div class="flex flex-col gap-2 mt-6 mb-6">
          <div class="flex gap-3 items-end">
            <h3 class="text-md leading-5 font-medium text-black">Preview Statement</h3>
            <span class="text-sm leading-[18px] text-gray-700 italic">Edits cannot be made until you insert it using the button below.</span>
          </div>
          <div class="border border-dashed border-gray-700 rounded-xs px-3 py-2 text-base leading-6 text-gray-900 min-h-[100px] bg-gray-50">
            <div v-if="isGenerating" class="flex flex-col items-center justify-center gap-3 h-full py-8">
              <span class="material-icons-round text-5xl text-blue-600 animate-spin" aria-hidden="true">autorenew</span>
              <p class="text-sm text-gray-600">Generating impact statement...</p>
            </div>
            <div v-else-if="selectedImpactUuids.length === 0" class="text-gray-900">
              {{ studentName }} is impacted by...
            </div>
            <div v-else class="[&_p]:mb-2 [&_p:last-child]:mb-0" v-html="previewStatement"></div>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2">
          <GbButton
            :label="insertButtonLabel"
            @click="handleInsert"
            :disabled="isGenerating"
            size="md"
            gbTheme="bluePrimary"
          >
            <span class="material-icons-round text-xl mr-1" aria-hidden="true">add_circle</span>
            {{ insertButtonLabel }}
          </GbButton>
          <GbButton
            label="Cancel"
            @click="$emit('close')"
            size="md"
            gbTheme="grayTertiary"
          />
        </div>
      </div>
    </template>
  </GbDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import Checkbox from 'primevue/checkbox'
import ProgressSpinner from 'primevue/progressspinner'
import { generateIODStatements } from '@/impact-of-disability/api'
import { buildIODStatement, populatePlaceholderText } from '@/impact-of-disability/statement-builder'
import type { ImpactArea, StatementForDisability } from '@/impact-of-disability/impact-of-disability_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { announce, clearAnnouncement } from '@/util/live_announcer'

const props = defineProps<{
  visible: boolean
  subjectLabel: string
  studentName: string
  disabilityUuid: string
  disabilityLabel: string
  impactAreas: ImpactArea[]
}>()

const emit = defineEmits<{
  close: []
  insert: [impactAreaUuids: string[], statementText: string]
}>()

const selectedImpactUuids = ref<string[]>([])
const generatedStatements = ref<StatementForDisability[]>([])
const isGenerating = ref(false)
const generationError = ref('')
const showSelectionError = ref(false)
const groupRef = ref<HTMLElement>()
let abortController: AbortController | null = null
const analytics = getAnalyticsTracker()
let previousImpactUuids: string[] = []
let suppressImpactChangeAnalytics = false

const impactOptions = computed(() => {
  return props.impactAreas
    .sort((a, b) => parseInt(a.area_order) - parseInt(b.area_order))
    .map(area => ({
      label: area.area_label,
      value: area.area_uuid
    }))
})

const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()

const previewStatement = computed(() => {
  if (selectedImpactUuids.value.length === 0 || isGenerating.value) return ''
  
  // Get selected impact areas
  const selectedAreas = props.impactAreas.filter(area => 
    selectedImpactUuids.value.includes(area.area_uuid)
  )
  
  // Use buildIODStatement to combine statements properly
  const [combinedStatement] = buildIODStatement({
    statementsForDisability: generatedStatements.value,
    selectedSubjects: [props.subjectLabel],
    selectedImpactAreas: selectedAreas
  })
  
  // Replace [studentName] placeholder with actual student name
  return populatePlaceholderText(combinedStatement, { studentName: props.studentName })
})

const insertButtonLabel = computed(() => {
  if (isGenerating.value) return 'Generating...'
  return 'Insert Impact Statement'
})

watch(isGenerating, generating => {
  if (generating) {
    announce('Generating impact statement...')
  } else {
    // Generation can finish or fail inside announce()'s restore delay, leaving a
    // "Generating..." restore pending after the spinner is already gone.
    clearAnnouncement('polite')
  }
})

watch(selectedImpactUuids, newImpacts => {
  if (newImpacts.length > 0) showSelectionError.value = false
})

// Watch for changes in selected impacts and generate statements
watch(selectedImpactUuids, async (newImpacts) => {
  // Skip analytics if this is a programmatic reset
  if (suppressImpactChangeAnalytics) {
    previousImpactUuids = [...newImpacts]
    suppressImpactChangeAnalytics = false
    return
  }
  
  // Track which items were added or removed for analytics
  const addedUuids = newImpacts.filter(uuid => !previousImpactUuids.includes(uuid))
  const removedUuids = previousImpactUuids.filter(uuid => !newImpacts.includes(uuid))
  
  // Send analytics events for added impact areas
  addedUuids.forEach(uuid => {
    const impactArea = props.impactAreas.find(area => area.area_uuid === uuid)
    if (impactArea) {
      analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.SELECTED_IMPACT_AREA, {
        iod_selected_disability: props.disabilityLabel,
        iod_selected_impact_area: impactArea.area_label
      })
    }
  })
  
  // Send analytics events for removed impact areas
  removedUuids.forEach(uuid => {
    const impactArea = props.impactAreas.find(area => area.area_uuid === uuid)
    if (impactArea) {
      analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.REMOVED_IMPACT_AREA, {
        iod_selected_disability: props.disabilityLabel,
        iod_selected_impact_area: impactArea.area_label
      })
    }
  })
  
  // Update previous impacts for next comparison
  previousImpactUuids = [...newImpacts]
  
  // Cancel any ongoing request
  if (abortController) {
    abortController.abort()
    abortController = null
  }
  
  // Clear previous statements immediately
  generatedStatements.value = []
  
  if (newImpacts.length === 0) {
    isGenerating.value = false
    return
  }
  
  await generateStatements()
}, { deep: true })

// Reset when modal closes
watch(() => props.visible, (isVisible) => {
  if (!isVisible) {
    // Cancel any ongoing request
    if (abortController) {
      abortController.abort()
      abortController = null
    }
    suppressImpactChangeAnalytics = true
    selectedImpactUuids.value = []
    generatedStatements.value = []
    generationError.value = ''
    showSelectionError.value = false
  }
})

async function generateStatements() {
  if (selectedImpactUuids.value.length === 0) return
  
  // Create new AbortController for this request
  abortController = new AbortController()
  const signal = abortController.signal
  
  isGenerating.value = true
  generationError.value = ''
  
  try {
    // Convert UUIDs to labels for the API
    const impactAreaLabels = selectedImpactUuids.value
      .map(uuid => props.impactAreas.find(area => area.area_uuid === uuid)?.area_label)
      .filter((label): label is string => !!label)
    
    const response = await generateIODStatements({
      disability: props.disabilityLabel,
      subjects: [props.subjectLabel],
      impact_areas: impactAreaLabels,
      student_name: props.studentName || '',
      content_profile: contentProfileStore.profileId,
      beta_only: visibilityStore.betaVisibility
    })
    
    // Check if request was aborted
    if (signal.aborted) return
    
    // Transform API response to StatementForDisability format
    if (response.statements) {
      generatedStatements.value = response.statements.map(stmt => {
        const matchingArea = props.impactAreas.find(area => area.area_label === stmt.impact_area)
        return {
          subject: stmt.subject,
          area_uuid: matchingArea?.area_uuid || stmt.impact_area,
          statement_text: stmt.statement_text
        }
      })
    }
  } catch (error: any) {
    // Don't show error if request was aborted
    if (error.name === 'AbortError' || signal.aborted) return
    
    console.error('Failed to generate IoD statements:', error)
    generationError.value = 'Failed to generate statement. Please try again.'
    announce(generationError.value, { assertive: true })
  } finally {
    if (!signal.aborted) {
      isGenerating.value = false
    }
  }
}

function handleInsert() {
  if (selectedImpactUuids.value.length === 0) {
    showSelectionError.value = true
    announce('Select at least 1 impacted area above', { assertive: true })
    nextTick(() => groupRef.value?.focus())
    return
  }
  
  if (previewStatement.value) {
    // Get the labels of selected impact areas for analytics
    const selectedImpactAreaLabels = selectedImpactUuids.value
      .map(uuid => props.impactAreas.find(area => area.area_uuid === uuid)?.area_label)
      .filter((label): label is string => !!label)
    
    analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.INSERTED_IMPACT_STATEMENT, {
      iod_selected_disability: props.disabilityLabel,
      iod_impact_areas: selectedImpactAreaLabels
    })
    
    emit('insert', selectedImpactUuids.value, previewStatement.value)
    selectedImpactUuids.value = []
    generatedStatements.value = []
    showSelectionError.value = false
  }
}
</script>
